'use client'

import { useState, useContext } from 'react'
import { Dialog } from '@vinted/web-ui'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  createReturnShipment,
  ComplaintModel,
  ReturnShippingMethodPayingSide,
} from '@marketplace-web/complaints/complaints-data'
import { AddressFormType } from '@marketplace-web/address/address-data'

import { ShippingContactType } from '@marketplace-web/shipping/shipping-contact-data'

import { ModalKeys } from './constants'
import Intro from './Intro'
import TotalRefundModal from '../TotalRefundModal'
import RequestReturnDetailsContext from '../RequestReturn/RequestReturnProvider/RequestReturnContext'
import { addressFieldConfiguration } from '../utils/address-field-configuration'
import ShippingDiscountModal from '../ShippingDiscountModal'
import AddressModal from '../AddressModal'

import ShippingContactModal from '../ShippingContactModal'

type Props = {
  complaint?: ComplaintModel | null
  conversationTransactionIsBundle?: boolean
  transactionId?: number
  url?: string
  isDataLoading: boolean
  onClose: () => void
  onProceedToCheckout: () => void
  onFetchReturnShipment: () => void
  onConfirm?: () => void
  onUrlNavigation?: () => void
}

const RequestReturnModal = ({
  transactionId,
  conversationTransactionIsBundle,
  complaint,
  url,
  onClose,
  isDataLoading,
  onProceedToCheckout,
  onFetchReturnShipment,
  onConfirm,
  onUrlNavigation,
}: Props) => {
  const [activeModal, setActiveModal] = useState<ModalKeys | null>(ModalKeys.Intro)
  const [isCreateReturnShipmentLoading, setIsCreateReturnShipmentLoading] = useState(false)

  const { safeBack } = useBrowserNavigation()

  const { requestReturnDetails: returnShipment, selectedPaymentSide } = useContext(
    RequestReturnDetailsContext,
  )

  const proceedToCheckout = selectedPaymentSide === ReturnShippingMethodPayingSide.Seller

  const closeModal = () => {
    setActiveModal(ModalKeys.Intro)
  }

  const handleAddressModalOpen = () => {
    setActiveModal(ModalKeys.AddressModal)
  }

  const handleShippingContactClick = () => {
    setActiveModal(ModalKeys.ShippingContactModal)
  }

  const handleShippingDiscountClick = () => {
    setActiveModal(ModalKeys.ShippingDiscountModal)
  }

  const handleTotalRedundInfoClick = () => {
    setActiveModal(ModalKeys.TotalRefundModal)
  }

  const handleInfoModalClose = () => {
    setActiveModal(ModalKeys.Intro)
  }

  const handleConfirm = async () => {
    onConfirm?.()
    if (proceedToCheckout) {
      onProceedToCheckout()

      return
    }

    if (!transactionId) return

    setIsCreateReturnShipmentLoading(true)

    const response = await createReturnShipment(transactionId)

    setIsCreateReturnShipmentLoading(false)

    if ('errors' in response) {
      return
    }

    if (url) {
      onUrlNavigation?.()
      safeBack(url)

      return
    }

    onClose()
  }

  const handleSubmit = () => {
    onFetchReturnShipment()

    closeModal()
  }

  const renderLoader = () => {
    return <ContentLoader size={ContentLoader.Size.Large} testId="request-return-modal-loader" />
  }

  const renderActiveModal = () => {
    switch (activeModal) {
      case ModalKeys.Intro:
        return (
          returnShipment && (
            <Intro
              transactionId={transactionId}
              returnShipment={returnShipment}
              complaint={complaint}
              conversationTransactionIsBundle={conversationTransactionIsBundle}
              isLoading={isCreateReturnShipmentLoading}
              onClose={onClose}
              onConfirm={handleConfirm}
              onAddressModalOpen={handleAddressModalOpen}
              onShippingContactClick={handleShippingContactClick}
              onShippingDiscountClick={handleShippingDiscountClick}
              onTotalRefundClick={handleTotalRedundInfoClick}
            />
          )
        )
      case ModalKeys.AddressModal:
        return (
          <AddressModal
            isOpen
            address={returnShipment?.userAddress}
            transactionId={transactionId}
            fieldConfiguration={addressFieldConfiguration}
            translationPrefix="conversation.get_shipping_label.modal.address"
            addressFormType={AddressFormType.GenerateLabelAddress}
            isPending={false}
            shippingOrderId={returnShipment?.shippingOrderId}
            onClose={closeModal}
            onSubmit={handleSubmit}
          />
        )
      case ModalKeys.ShippingContactModal:
        return (
          transactionId && (
            <ShippingContactModal
              isOpen
              transactionId={transactionId}
              type={ShippingContactType.Seller}
              phoneNumber={returnShipment?.phoneNumber}
              shippingOrderId={returnShipment?.shippingOrderId}
              onClose={handleSubmit}
            />
          )
        )
      case ModalKeys.ShippingDiscountModal:
        return (
          <ShippingDiscountModal
            isOpen
            onClose={handleInfoModalClose}
            discount={returnShipment?.pickupPointDetails?.discount}
            lowestPriceIn30Days={returnShipment?.pickupPointDetails?.lowestPriceIn30Days}
          />
        )
      case ModalKeys.TotalRefundModal:
        return (
          complaint && (
            <TotalRefundModal
              isOpen
              isBundleOrder={complaint.isBundleOrder}
              onClose={closeModal}
              refundEstimate={complaint.estimatedRelease}
              isOpenedInDialog
            />
          )
        )
      default:
        return null
    }
  }

  if (isDataLoading) return <Dialog show>{renderLoader()}</Dialog>

  return renderActiveModal()
}

export default RequestReturnModal
